'use client'

import * as React from 'react'

import { Cross1Icon } from '@radix-ui/react-icons'
import { clsx } from 'clsx'
import { Button } from '../button'
import { Popover, PopoverContent, PopoverTrigger } from '../popover'
import { Input, InputProps } from '../input'
import { useToggleState } from '~/hooks/use-toggle-state'
import { ScrollArea } from '../scroll-area'
import { IconButton } from '../icon-button'
import { mergeRefs } from '~/lib/merge-refs'
import { useVirtualizer } from '@tanstack/react-virtual'

type Option = { id: string; label: string; icon: string }
type Tab = { id: string; label: string; items: Option[] }

export const ComboboxWithTabs = (props: {
  tabs?: Tab[]
  allTab?: boolean
  className?: string
  ref?: React.RefObject<null | HTMLInputElement>
  inputProps?: InputProps
  handleSelect: (option: Option) => void
}) => {
  const listRef = React.useRef<HTMLDivElement>(null)
  const toggleState = useToggleState()
  const [query, setQuery] = React.useState('')

  // Improved search function to filter options
  const filterOptions = (options: Option[], searchQuery: string): Option[] => {
    const normalizedQuery = searchQuery.toLowerCase().trim()

    const opts: Option[] = []
    options.forEach((option) => {
      if (option.label.toLowerCase().includes(normalizedQuery)) {
        opts.push(option)
      }
    })

    return opts
  }

  // Create all options if allTab is true
  const allOptions = React.useMemo(() => {
    const tabs = props.tabs ?? []

    return props.allTab
      ? tabs.reduce<Option[]>((acc, tab) => {
          const uniqueOptions = new Set([
            ...acc,
            ...tab.items.map((item) => ({
              ...item,
              label: `${item.label} (${tab.label})`,
              icon: item.icon,
              tabId: tab.id,
            })),
          ])

          return Array.from(uniqueOptions)
        }, [])
      : []
  }, [props.allTab, props.tabs])

  // Comprehensive filtering across all tabs
  const filteredTabs = React.useMemo(() => {
    const normalizedQuery = query.toLowerCase().trim()
    const tabs = props.tabs ?? []

    // If allTab is true, create a special "All" tab
    if (props.allTab) {
      const filteredAllOptions = filterOptions(allOptions, normalizedQuery)
      const allTab = {
        id: 'all',
        label: 'All',
        items: filteredAllOptions,
      }

      // Filter other tabs
      const filteredOtherTabs = tabs
        .map((tab) => {
          if (!tab.items.length) return null
          const options = filterOptions(tab.items, normalizedQuery)

          return { ...tab, items: options }
        })
        .filter(Boolean)

      return [allTab, ...filteredOtherTabs]
    }

    // If no allTab, just filter regular tabs
    return tabs
      .map((tab) => {
        if (!tab.items.length) return null
        const options = filterOptions(tab.items, normalizedQuery)
        if (!options.length) return null
        return {
          ...tab,
          items: options,
        }
      })
      .filter(Boolean)
  }, [query, props.tabs, props.allTab, allOptions])

  const [activeTab, setActiveTab] = React.useState(filteredTabs[0]?.id ?? 'all')
  const inputRef = React.useRef<HTMLInputElement>(null)
  const [cursor, setCursor] = React.useState(0)
  const [pointerIdle, setPointerIdle] = React.useState(true)
  const currentOptions =
    filteredTabs.find((tab) => tab.id === activeTab)?.items ?? []

  // Ensure active tab is valid after filtering
  React.useEffect(() => {
    if (!filteredTabs.some((tab) => tab.id === activeTab)) {
      setActiveTab(filteredTabs[0]?.id ?? 'all')
    }
  }, [filteredTabs, activeTab])

  React.useEffect(() => {
    if (!open || !pointerIdle) return

    // focus button for this cursor
    const buttonForCursor = document.querySelector<HTMLButtonElement>(
      `button[data-tab-option][data-state-active='true']`
    )
    buttonForCursor?.scrollIntoView({ behavior: 'instant', block: 'nearest' })
  }, [cursor, pointerIdle])

  React.useEffect(() => {
    // focus button for this cursor
    const buttonForTab = document.querySelector<HTMLButtonElement>(
      `button[data-tab][data-state-active='true']`
    )
    buttonForTab?.scrollIntoView({ behavior: 'instant', block: 'nearest' })
  }, [activeTab])

  React.useEffect(() => {
    if (!listRef.current) return
    if (cursor === 0) {
      listRef.current.scrollTo({
        top: 0,
        behavior: 'instant',
      })
    } else if (cursor === currentOptions.length - 1) {
      listRef.current.scrollTo({
        top: listRef.current.scrollHeight,
        behavior: 'instant',
      })
    }
  }, [currentOptions.length, cursor])

  React.useEffect(() => {
    if (!toggleState.isOn) return
    toggleState.handleOn()
  }, [query, toggleState])

  React.useEffect(() => {
    if (toggleState.isOn) return
    setQuery('')
    setCursor(0)
    setActiveTab(filteredTabs[0]?.id ?? 'all')
  }, [filteredTabs, query, toggleState])

  const refs = [inputRef]
  // eslint-disable-next-line react-compiler/react-compiler
  if (props.ref) refs.push(props.ref)

  return (
    <div className="relative">
      <Popover
        open={toggleState.isOn}
        onOpenChange={() => {
          setCursor(0)
          setActiveTab(filteredTabs[0]?.id ?? 'all')
        }}
      >
        <Input
          // eslint-disable-next-line react-compiler/react-compiler
          ref={mergeRefs(refs)}
          className={clsx('pr-6 cursor-text', props.className)}
          {...props.inputProps}
          onFocus={() => {
            toggleState.handleOn()
          }}
          disabled={props.inputProps?.disabled}
          onClick={toggleState.handleOn}
          value={query}
          onChange={(e) => {
            setQuery(e.currentTarget.value)
            setCursor(0)
          }}
          onBlur={(e) => {
            const focusIsInPopover = e.relatedTarget?.closest(
              '[data-popover-content]'
            )
            if (focusIsInPopover) return
            toggleState.handleOff()
          }}
          onPaste={(e) => {
            e.stopPropagation()
          }}
          onKeyDown={(e) => {
            setPointerIdle(true)

            switch (e.key) {
              case 'Escape': {
                if (!toggleState.isOn) inputRef.current?.blur()
                else toggleState.handleOff()
                break
              }
              case 'Enter': {
                if (!toggleState.isOn) {
                  toggleState.handleOn()
                  break
                }

                const buttonForCursor =
                  document.querySelector<HTMLButtonElement>(
                    `[data-tab-option][data-state-active='true']`
                  )
                if (!buttonForCursor) break
                buttonForCursor.click()

                break
              }
              case 'ArrowDown': {
                if (e.metaKey) {
                  if (listRef.current) {
                    listRef.current.scrollTo({
                      top: listRef.current.scrollHeight,
                      behavior: 'instant',
                    })
                  }
                  setCursor(currentOptions.length - 1)
                } else {
                  setCursor((i) => (i + 1) % currentOptions.length)
                }
                break
              }
              case 'ArrowUp': {
                if (e.metaKey) {
                  setCursor(0)
                } else {
                  setCursor(
                    (i) =>
                      (i + currentOptions.length - 1) % currentOptions.length
                  )
                }
                break
              }
              case 'ArrowLeft': {
                if (toggleState.isOn) e.preventDefault()
                let newCategoryItems = [...currentOptions]
                setActiveTab((prev) => {
                  const index = filteredTabs.findIndex((tab) => tab.id === prev)
                  const newCategory =
                    filteredTabs[index - 1] ??
                    filteredTabs[filteredTabs.length - 1]

                  if (!newCategory) return prev
                  newCategoryItems = newCategory.items
                  return newCategory.id
                })
                if (newCategoryItems.length - 1 < cursor) {
                  setCursor((i) =>
                    i > newCategoryItems.length - 1
                      ? newCategoryItems.length - 1
                      : i
                  )
                }
                break
              }
              case 'ArrowRight': {
                if (toggleState.isOn) e.preventDefault()
                let newCategoryItems = [...currentOptions]
                setActiveTab((prev) => {
                  const index = filteredTabs.findIndex((tab) => tab.id === prev)
                  const newCategory =
                    filteredTabs[index + 1] ??
                    filteredTabs[filteredTabs.length + 1] ??
                    filteredTabs[0]

                  if (!newCategory) return prev
                  newCategoryItems = newCategory.items
                  return newCategory.id
                })
                if (newCategoryItems.length - 1 < cursor) {
                  setCursor((i) =>
                    i > newCategoryItems.length - 1
                      ? newCategoryItems.length - 1
                      : i
                  )
                }
                break
              }
              default:
                if (toggleState.isOn) return
                toggleState.handleOn()
                break
            }
          }}
        />
        <PopoverTrigger className="h-full invisible pointer-events-none absolute inset-y-0 right-0" />

        {!!query && (
          <IconButton
            onClick={() => setQuery('')}
            className="absolute top-1/2 -translate-y-1/2 -translate-x-[5px] right-0"
          >
            <Cross1Icon className="size-3" />
          </IconButton>
        )}
        <PopoverContent
          onOpenAutoFocus={(e) => e.preventDefault()}
          className="w-[420px] max-h-[258px] !p-0 flex flex-col outline-none"
          align="end"
          side="bottom"
          data-popover-content
          forceMount
          onMouseMove={() => {
            setPointerIdle(false)
          }}
        >
          <ScrollArea
            variant="subtle"
            orientation="horizontal"
            className="w-full justify-start rounded-none h-[38px] bg-dark-base-dark shrink-0"
            viewportClassName="px-2 border-b border-dark-border-solid"
            scrollBarClassName="hidden"
          >
            {filteredTabs.map((tab) => (
              <Button
                unstyled
                focusStyled
                data-tab
                onPointerDown={(e) => {
                  e.preventDefault()
                  setActiveTab(tab.id)
                }}
                key={tab.id}
                value={tab.id}
                data-state-active={activeTab === tab.id}
                className={clsx(
                  "data-[state-active='false']:opacity-30 text-sm font-medium text-dark-bright px-2.5 py-2 data-[state-active='false']:hover:opacity-100 cursor-default relative h-full",
                  "data-[state-active='true']:after:content-[''] data-[state-active='true']:after:w-full data-[state-active='true']:after:absolute data-[state-active='true']:after:left-0 data-[state-active='true']:after:bottom-0 data-[state-active='true']:after:h-px data-[state-active='true']:after:bg-dark-control-base data-[state-active='true']:after:pointer-events-none"
                )}
              >
                {tab.label}{' '}
                <span className="ml-1 bg-muted px-1.5 text-xs bg-dark-shade-hover rounded-full tabular-nums">
                  {tab.items.length}
                </span>
              </Button>
            ))}
          </ScrollArea>
          {currentOptions.length === 0 ? (
            <>
              {!query ? (
                <div className="flex justify-center items-center h-[218px] text-sm text-dark-muted px-4">
                  No results found.
                </div>
              ) : (
                <div className="flex justify-center items-center h-[218px] text-sm text-dark-muted px-4">
                  <p>
                    <span className="whitespace-nowrap">
                      No results found for &nbsp;
                    </span>
                    <span className="text-dark-bright font-medium break-all line-clamp-2">
                      "{query}"
                    </span>
                  </p>
                </div>
              )}
            </>
          ) : (
            !!toggleState.isOn && (
              <VirtualizedList
                ref={listRef}
                options={currentOptions}
                getIsActive={(index) => cursor === index}
                onPointerUp={() => {
                  if (!inputRef.current) return
                  inputRef.current.focus()
                }}
                itemProps={{
                  onPointerOver: (index) => {
                    if (pointerIdle) return
                    setCursor(index)
                  },
                  onPointerDown: (item) => {
                    props.handleSelect(item)
                    setQuery('')
                    toggleState.handleOff()
                  },
                  onClick: (item) => {
                    props.handleSelect(item)
                    setQuery('')
                    toggleState.handleOff()
                  },
                }}
              />
            )
          )}
        </PopoverContent>
      </Popover>
    </div>
  )
}

const VirtualizedList = <T extends Option>({
  ref,
  options,
  getIsActive,
  itemProps,
  onPointerUp,
}: {
  ref: React.RefObject<null | HTMLDivElement>
  options: T[]
  getIsActive: (index: number) => boolean
  itemProps: Omit<
    React.JSX.IntrinsicElements['button'],
    'onPointerOver' | 'onClick' | 'onPointerDown'
  > & {
    onPointerOver: (index: number) => void
    onPointerDown: (item: T) => void
    onClick: (item: T) => void
  }
  onPointerUp: React.MouseEventHandler<HTMLDivElement>
}) => {
  'use no memo'

  const rowVirtualizer = useVirtualizer({
    count: options.length,
    getScrollElement: () => ref.current,
    estimateSize: () => 36,
    overscan: 4,
  })

  return (
    <ScrollArea
      viewportRef={ref}
      viewportClassName="max-h-[215px] h-fit overflow-auto overflow-x-hidden"
      onPointerUp={onPointerUp}
    >
      <div
        style={{
          height: `${rowVirtualizer.getTotalSize()}px`,
          width: '100%',
          position: 'relative',
        }}
      >
        {rowVirtualizer.getVirtualItems().map((virtualRow) => {
          const index = virtualRow.index
          const item = options[index]
          if (!item) return null

          return (
            <Button
              unstyled
              data-tab-option
              className="data-[state-active='true']:bg-dark-shade cursor-default text-left text-xs border-b border-dark-border-solid last:border-none pl-[18px] flex items-center py-2 w-full gap-x-2"
              key={virtualRow.key}
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: `${virtualRow.size}px`,
                transform: `translateY(${virtualRow.start}px)`,
              }}
              data-state-active={getIsActive(index)}
              {...itemProps}
              onPointerOver={() => itemProps.onPointerOver(index)}
              onPointerDown={() => itemProps.onPointerDown(item)}
              onClick={() => itemProps.onClick(item)}
            >
              {typeof item.icon === 'string' ? (
                // eslint-disable-next-line @next/next/no-img-element
                <img
                  key={item.id}
                  alt={item.label}
                  src={item.icon}
                  className="dark:invert"
                  loading="eager"
                  width={14}
                  height={14}
                />
              ) : (
                item.icon
              )}
              {item.label}
            </Button>
          )
        })}
      </div>
    </ScrollArea>
  )
}
